import { useRef } from 'react'
import { motion, useMotionValue, useSpring, useTransform, useReducedMotion } from 'framer-motion'
import { ArrowRight, Star, MapPin, Search } from 'lucide-react'

const ease = [0.16, 1, 0.3, 1] as const

const reveal = {
  hidden: { opacity: 0, y: 24 },
  show: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: 0.1 + i * 0.08, ease },
  }),
}

const CREDS = [
  { k: 'From $1,500', v: 'Fixed price, no surprises' },
  { k: '5 to 7 days', v: 'Typical Starter build' },
  { k: 'You own it', v: 'No lock-in, no rebuilds' },
  { k: 'Melbourne', v: 'Local, and we answer' },
]

const Hero = () => {
  const stageRef = useRef<HTMLDivElement>(null)
  const reduce = useReducedMotion()

  const mx = useMotionValue(0)
  const my = useMotionValue(0)
  const rotY = useSpring(useTransform(mx, [-0.5, 0.5], [-10, 10]), { stiffness: 120, damping: 18 })
  const rotX = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), { stiffness: 120, damping: 18 })

  const onMove = (e: React.MouseEvent) => {
    if (reduce || !stageRef.current) return
    const r = stageRef.current.getBoundingClientRect()
    mx.set((e.clientX - r.left) / r.width - 0.5)
    my.set((e.clientY - r.top) / r.height - 0.5)
  }

  const scrollTo = (id: string) => document.querySelector(id)?.scrollIntoView({ behavior: 'smooth' })

  return (
    <section className="relative flex min-h-[100dvh] flex-col overflow-hidden">
      {/* Atmosphere */}
      <div className="pointer-events-none absolute inset-0">
        <div className="bg-fine-grid absolute inset-0 opacity-40" />
        <div className="absolute -left-40 top-10 h-[520px] w-[520px] rounded-full bg-gold-bright/10 blur-[120px]" />
        <div className="absolute -right-32 bottom-0 h-[480px] w-[480px] rounded-full bg-[#2563eb]/[0.06] blur-[120px]" />
      </div>

      <div className="relative z-10 flex flex-1 items-start lg:items-center">
      <div className="mx-auto grid w-full max-w-container grid-cols-1 items-center gap-12 px-5 pb-12 pt-28 lg:grid-cols-[1.05fr_0.95fr] lg:gap-14 lg:px-8 lg:pb-0 lg:pt-0">
        {/* Left — copy */}
        <div className="text-center lg:text-left">
          <motion.p custom={0} variants={reveal} initial="hidden" animate="show" className="eyebrow">
            Melbourne web design
          </motion.p>

          <motion.h1
            custom={1}
            variants={reveal}
            initial="hidden"
            animate="show"
            className="font-display mt-5 text-[2rem] font-extrabold leading-[1.05] tracking-tight text-ink sm:text-5xl lg:text-[4.25rem] lg:leading-[0.98]"
          >
            Web design in Melbourne,{' '}
            <span className="relative inline-block">
              <span className="ink-gold">without the agency price tag.</span>
              <svg className="absolute -bottom-2 left-0 h-3 w-full" viewBox="0 0 300 12" fill="none" preserveAspectRatio="none">
                <motion.path
                  d="M3 8C70 2 230 2 297 8"
                  stroke="#d4af37"
                  strokeWidth="4"
                  strokeLinecap="round"
                  initial={{ pathLength: 0, opacity: 0 }}
                  animate={{ pathLength: 1, opacity: 1 }}
                  transition={{ duration: 0.9, delay: 0.7, ease }}
                />
              </svg>
            </span>
          </motion.h1>

          <motion.p
            custom={2}
            variants={reveal}
            initial="hidden"
            animate="show"
            className="mx-auto mt-7 max-w-xl text-lg text-ink-soft lg:mx-0"
          >
            Fast, fixed-price websites from $1,500 for Melbourne tradies, clinics, cafes,
            and local service businesses.
          </motion.p>

          <motion.div
            custom={3}
            variants={reveal}
            initial="hidden"
            animate="show"
            className="mt-9 flex flex-col items-center gap-3 sm:flex-row lg:items-start lg:justify-start"
          >
            <button onClick={() => scrollTo('#contact')} className="btn btn-primary group w-full sm:w-auto">
              Get a fixed-price quote
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </button>
            <button onClick={() => scrollTo('#pricing')} className="btn btn-ghost w-full sm:w-auto">
              See pricing
            </button>
          </motion.div>
        </div>

        {/* Right — 3D crate */}
        <motion.div
          ref={stageRef}
          onMouseMove={onMove}
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, delay: 0.25, ease }}
          className="relative mx-auto hidden aspect-square w-full max-w-[460px] lg:block"
          style={{ perspective: 1100 }}
        >
          <motion.div style={{ rotateX: rotX, rotateY: rotY, transformStyle: 'preserve-3d' }} className="relative h-full w-full">
            <CrateArt />

            {/* Floating local-search card */}
            <motion.div
              animate={reduce ? undefined : { y: [0, -12, 0] }}
              transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
              className="surface absolute -left-6 top-10 flex items-center gap-3 rounded-2xl px-4 py-3"
              style={{ transform: 'translateZ(60px)' }}
            >
              <span className="grid h-9 w-9 place-items-center rounded-xl bg-gold/10 text-gold">
                <Search className="h-4 w-4" />
              </span>
              <div className="text-left">
                <p className="text-[0.7rem] font-semibold uppercase tracking-wider text-ink-faint">Ranked for</p>
                <p className="text-sm font-semibold text-ink">"plumber near me"</p>
              </div>
            </motion.div>

            {/* Floating review card */}
            <motion.div
              animate={reduce ? undefined : { y: [0, 14, 0] }}
              transition={{ duration: 4.4, repeat: Infinity, ease: 'easeInOut', delay: 0.6 }}
              className="surface absolute -right-4 bottom-14 flex items-center gap-3 rounded-2xl px-4 py-3"
              style={{ transform: 'translateZ(80px)' }}
            >
              <span className="grid h-9 w-9 place-items-center rounded-xl bg-ink text-cream">
                <MapPin className="h-4 w-4" />
              </span>
              <div className="text-left">
                <div className="flex gap-0.5 text-amber-500">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <Star key={i} className="h-3 w-3 fill-current" />
                  ))}
                </div>
                <p className="text-sm font-semibold text-ink">New enquiry from Google</p>
              </div>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
      </div>

      {/* Credibility strip — normal flow at the bottom of the hero column */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.7, ease }}
        className="relative z-10 border-t border-[var(--line)] bg-cream/60 backdrop-blur-sm"
      >
        <div className="mx-auto grid max-w-container grid-cols-2 divide-x divide-[var(--line)] px-5 lg:grid-cols-4 lg:px-8">
          {CREDS.map((c) => (
            <div key={c.k} className="px-4 py-5 lg:px-6">
              <p className="font-display text-lg font-bold text-ink">{c.k}</p>
              <p className="mt-0.5 text-[0.82rem] text-ink-faint">{c.v}</p>
            </div>
          ))}
        </div>
      </motion.div>
    </section>
  )
}

/** Isometric crate built from layered faces — brand blue, matches the logo. */
const CrateArt = () => (
  <svg viewBox="0 0 400 400" className="h-full w-full drop-shadow-[0_30px_50px_rgba(37,99,235,0.28)]">
    <defs>
      <linearGradient id="cLeft" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#2563eb" />
        <stop offset="1" stopColor="#1e40af" />
      </linearGradient>
      <linearGradient id="cRight" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#3b82f6" />
        <stop offset="1" stopColor="#60a5fa" />
      </linearGradient>
      <linearGradient id="cTop" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#60a5fa" />
        <stop offset="1" stopColor="#93c5fd" />
      </linearGradient>
    </defs>

    <ellipse cx="200" cy="350" rx="135" ry="22" fill="#1e3a8a" opacity="0.14" />

    {/* top */}
    <path d="M200 70 330 132 200 196 70 132Z" fill="url(#cTop)" />
    {/* left */}
    <path d="M70 132 200 196 200 332 70 268Z" fill="url(#cLeft)" />
    {/* right */}
    <path d="M330 132 200 196 200 332 330 268Z" fill="url(#cRight)" />

    {/* code lines on left face */}
    <g stroke="#ffffff" strokeLinecap="round">
      <line x1="92" y1="178" x2="178" y2="220" strokeWidth="6" opacity="0.65" />
      <line x1="92" y1="206" x2="158" y2="240" strokeWidth="6" opacity="0.45" />
      <line x1="92" y1="234" x2="172" y2="276" strokeWidth="6" opacity="0.3" />
    </g>
    {/* sky-blue seam on right face */}
    <path d="M214 214 318 158 318 176 214 232Z" fill="#bfdbfe" opacity="0.7" />
    {/* light-blue corner accents */}
    <circle cx="84" cy="150" r="5" fill="#93c5fd" />
    <circle cx="316" cy="150" r="5" fill="#93c5fd" />
    <circle cx="200" cy="318" r="5" fill="#93c5fd" />
  </svg>
)

export default Hero
